import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Breadcrumb, Dropdown, Spinner } from 'react-bootstrap';
import { useDebounce } from 'use-debounce';
import * as constants from 'Constants';
import httpService from 'services/httpService';
import FileDownload from 'js-file-download';
import { toast } from 'react-toastify';
import log from 'loglevel';

import {
  downloadAllSubmissions,
  downloadEvaluateResult,
  checkEvaluationStatus,
  evaluateClabSubmissions,
} from 'Navigation/Course/Content/Labs/Instructor/activityUtility';

import { FiUsers, FiSearch, FiChevronDown, FiDownload, FiCheck } from 'react-icons/fi';
import { MdOutlinePending } from 'react-icons/md';

import styles from '../ActivitySubmissionsV2/ActivitySubmissionsV2.module.css';

const StatCard = memo(({ label, value, icon: Icon, colorClass }) => (
  <div className={styles.statCard}>
    <div className={styles.statCardContent}>
      <span className={styles.statCardLabel}>{label}</span>
      <span className={`${styles.statCardValue} ${styles[colorClass]}`}>{value}</span>
    </div>
    <div className={`${styles.statCardIcon} ${styles[colorClass + 'Bg']}`}>
      <Icon size={18} />
    </div>
  </div>
));

export default function VirtualSubmissionsV2() {
  const { courseId, labId, activityID } = useParams();
  const location = useLocation();

  const urlArray = location.pathname.split('/');
  const activityId = activityID || urlArray[8];
  const labID = labId || urlArray[6];

  const [submissions, setSubmissions] = useState([]);
  const [activityTitle, setActivityTitle] = useState('Activity');
  const [labTitle, setLabTitle] = useState('Lab');
  const [loading, setLoading] = useState(true);
  const [permissionError, setPermissionError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const [sortField, setSortField] = useState('owner_name');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    const fetchActivityTitle = async () => {
      if (!activityId) return;
      try {
        const response = await httpService.get(`${constants.base_url}/clab/activity/${activityId}/`);
        setActivityTitle(response.data?.name || 'Activity');
      } catch (error) {
        log.error('Error fetching cLab activity title:', error);
      }
    };
    fetchActivityTitle();
  }, [activityId]);

  useEffect(() => {
    const fetchLabTitle = async () => {
      if (!courseId || !labID) return;
      try {
        const response = await httpService.get(`${constants.base_url}/lab/all/${labID}?courseId=${courseId}`);
        setLabTitle(response.data.lab?.title || 'Lab');
      } catch (error) {
        log.error('Error fetching lab title:', error);
      }
    };
    fetchLabTitle();
  }, [labID, courseId]);

  const fetchSubmissions = useCallback(async () => {
    if (!activityId) return;
    setLoading(true);
    setPermissionError('');
    try {
      const url = `${constants.base_url}/clab/activity/submission_list/?activity_id=${activityId}&all_users=true`;
      const response = await httpService.get(url);
      setSubmissions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      log.error('Error fetching cLab submissions:', error);
      if (error.response?.status === 403) {
        setPermissionError('You do not have permission to view cLab submissions for this activity.');
      } else {
        toast.error('Error fetching submissions', { position: 'top-right', autoClose: 3000 });
      }
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [activityId]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const summary = useMemo(() => {
    const evaluated = submissions.filter((s) => s.is_evaluated).length;
    return {
      total: submissions.length,
      evaluated,
      pending: submissions.length - evaluated,
    };
  }, [submissions]);

  const visibleSubmissions = useMemo(() => {
    const query = debouncedSearchQuery.trim().toLowerCase();
    const filtered = query
      ? submissions.filter((s) =>
        (s.owner_name || '').toLowerCase().includes(query) ||
        (s.owner_email || '').toLowerCase().includes(query) ||
        (s.owner_username || '').toLowerCase().includes(query))
      : submissions;

    const direction = sortOrder === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const left = a[sortField];
      const right = b[sortField];
      if (left === right) return 0;
      if (left === null || left === undefined) return 1;
      if (right === null || right === undefined) return -1;
      if (typeof left === 'number' && typeof right === 'number') return (left - right) * direction;
      return String(left).localeCompare(String(right)) * direction;
    });
  }, [submissions, debouncedSearchQuery, sortField, sortOrder]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '';
    return sortOrder === 'asc' ? '▲' : '▼';
  };

  const downloadStudentSubmission = async (username) => {
    const url = `${constants.base_url}/clab/activity/submission_files/?activity_id=${activityId}&all_users=${username}`;
    try {
      const response = await httpService.instance({ url, method: 'GET', responseType: 'blob' });
      FileDownload(response.data, `Activity_${activityId}_${username}.tar.gz`);
    } catch (error) {
      log.error('Error downloading cLab submission:', error);
      toast.error('Error downloading submission', { position: 'top-right', autoClose: 3000 });
    }
  };

  const formatMarks = (value) => (value === null || value === undefined ? '-' : value);

  return (
    <div className={styles.container}>
      <Breadcrumb className={styles.breadcrumb}>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs` }}>
          Labs
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs/${labID}` }}>
          {labTitle}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>{activityTitle} — Submissions</Breadcrumb.Item>
      </Breadcrumb>

      <div className={styles.statsRow}>
        <StatCard label="Total Submissions" value={summary.total} icon={FiUsers} colorClass="blue" />
        <StatCard label="Evaluated" value={summary.evaluated} icon={FiCheck} colorClass="green" />
        <StatCard label="Evaluation Pending" value={summary.pending} icon={MdOutlinePending} colorClass="orange" />
      </div>

      <div className={styles.searchActionsBar}>
        <div className={styles.searchBar}>
          <FiSearch className={styles.searchIcon} size={16} />
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search by name, email or roll no"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className={styles.actionButtons}>
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => downloadAllSubmissions('virtual', activityId)}
            disabled={summary.total === 0}
          >
            <FiDownload size={16} /> Download All
          </button>

          <Dropdown>
            <Dropdown.Toggle className={styles.actionsButton} variant="light">
              Actions <FiChevronDown size={16} />
            </Dropdown.Toggle>
            <Dropdown.Menu className={styles.actionsDropdownMenu}>
              <Dropdown.Item onClick={() => evaluateClabSubmissions('virtual', activityId)}>
                Evaluate All Submissions
              </Dropdown.Item>
              <Dropdown.Item onClick={() => checkEvaluationStatus('virtual', activityId)}>
                Check Evaluation Status
              </Dropdown.Item>
              <Dropdown.Item onClick={() => downloadEvaluateResult('virtual', activityId)}>
                Download Evaluation Result (CSV)
              </Dropdown.Item>
              <Dropdown.Item onClick={fetchSubmissions}>Refresh</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.snoCol}>S.NO.</th>
                <th className={`${styles.rollCol} ${styles.sortable}`} onClick={() => handleSort('owner_username')}>
                  ROLL NO {getSortIcon('owner_username')}
                </th>
                <th className={`${styles.nameCol} ${styles.sortable}`} onClick={() => handleSort('owner_name')}>
                  NAME {getSortIcon('owner_name')}
                </th>
                <th className={`${styles.emailCol} ${styles.sortable}`} onClick={() => handleSort('owner_email')}>
                  EMAIL {getSortIcon('owner_email')}
                </th>
                <th className={`${styles.dateCol} ${styles.sortable}`} onClick={() => handleSort('submission_time')}>
                  SUBMITTED ON {getSortIcon('submission_time')}
                </th>
                <th className={styles.statusCol}>EVALUATION STATUS</th>
                <th className={`${styles.marksCol} ${styles.sortable}`} onClick={() => handleSort('evaluation_marks')}>
                  EVALUATION MARKS {getSortIcon('evaluation_marks')}
                </th>
                <th className={`${styles.marksCol} ${styles.sortable}`} onClick={() => handleSort('additional_marks')}>
                  ADDITIONAL MARKS {getSortIcon('additional_marks')}
                </th>
                <th className={styles.fileCol}>SUBMISSION</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className={styles.emptyState}>
                    <Spinner animation="border" size="sm" /> Loading submissions...
                  </td>
                </tr>
              ) : permissionError ? (
                <tr>
                  <td colSpan={9} className={styles.emptyState}>{permissionError}</td>
                </tr>
              ) : visibleSubmissions.length === 0 ? (
                <tr>
                  <td colSpan={9} className={styles.emptyState}>No submissions found</td>
                </tr>
              ) : (
                visibleSubmissions.map((submission, index) => (
                  <tr key={submission.id} className={styles.tableRow}>
                    <td className={styles.snoCell}>{index + 1}</td>
                    <td className={styles.rollCell}>{submission.owner_username || '-'}</td>
                    <td className={styles.nameCell}>{submission.owner_name || '-'}</td>
                    <td className={styles.emailCell}>{submission.owner_email || '-'}</td>
                    <td className={styles.dateCell}>{submission.submission_time || '-'}</td>
                    <td className={styles.statusCell}>
                      <span className={`${styles.statusBadge} ${submission.is_evaluated ? styles.statusSuccess : styles.statusWarning}`}>
                        {submission.is_evaluated ? 'Evaluated' : 'Pending'}
                      </span>
                    </td>
                    <td className={styles.marksCell}>{formatMarks(submission.evaluation_marks)}</td>
                    <td className={styles.marksCell}>{formatMarks(submission.additional_marks)}</td>
                    <td className={styles.fileCell}>
                      <button
                        type="button"
                        className={styles.fileLink}
                        onClick={() => downloadStudentSubmission(submission.owner_username)}
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
